import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { UserAuthProvider } from "./lib/userAuth";
import Landing from "./pages/Landing";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminLibrary from "./pages/AdminLibrary";
import AdminBooklets from "./pages/AdminBooklets";
import AdminSiteContent from "./pages/AdminSiteContent";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import BlogIndex from "./pages/BlogIndex";
import BlogPost from "./pages/BlogPost";
import AuthCallback from "./pages/AuthCallback";
import WhereShouldIStart from "./pages/WhereShouldIStart";
import TopicDetail from "./pages/TopicDetail";
import LibraryIndex from "./pages/LibraryIndex";
import LibraryDetail from "./pages/LibraryDetail";
import BookletSample from "./pages/BookletSample";

function Protected({ children }) {
    const { user, loading } = useAuth();
    if (loading || user === null)
        return (
            <div className="grid min-h-screen place-items-center bg-page text-ink/55">
                Checking session…
            </div>
        );
    if (user === false) return <Navigate to="/admin/login" replace />;
    return children;
}

function ScrollToHash() {
    const { hash, pathname } = useLocation();
    React.useEffect(() => {
        if (hash && !hash.startsWith("#session_id")) {
            const el = document.getElementById(hash.replace("#", ""));
            if (el) {
                setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
                return;
            }
        }
        window.scrollTo(0, 0);
    }, [hash, pathname]);
    return null;
}

// Fires a GA4 page_view on every SPA route change (React Router doesn't full-reload).
function GaPageView() {
    const { pathname, search } = useLocation();
    React.useEffect(() => {
        if (typeof window === "undefined" || typeof window.gtag !== "function") return;
        window.gtag("event", "page_view", {
            page_path: pathname + search,
            page_location: window.location.href,
            page_title: document.title,
        });
    }, [pathname, search]);
    return null;
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Detect session_id in the URL fragment synchronously during render so that
// the AuthCallback view handles the OAuth exchange BEFORE the AuthProvider
// tries to hit /auth/me (which would 401 and race).
function AppRouter() {
    const location = useLocation();
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/where-should-i-start" element={<WhereShouldIStart />} />
            <Route path="/where-should-i-start/:slug" element={<TopicDetail />} />
            <Route path="/library" element={<LibraryIndex />} />
            <Route path="/library/:slug" element={<LibraryDetail />} />
            <Route path="/booklets/:slug/read" element={<BookletSample />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
                path="/admin"
                element={
                    <Protected>
                        <AdminDashboard />
                    </Protected>
                }
            />
            <Route
                path="/admin/library"
                element={
                    <Protected>
                        <AdminLibrary />
                    </Protected>
                }
            />
            <Route
                path="/admin/booklets"
                element={
                    <Protected>
                        <AdminBooklets />
                    </Protected>
                }
            />
            <Route
                path="/admin/site-content"
                element={
                    <Protected>
                        <AdminSiteContent />
                    </Protected>
                }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

export default function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <UserAuthProvider>
                    <AuthProvider>
                        <ScrollToHash />
                        <GaPageView />
                        <Toaster
                            position="top-right"
                            theme="light"
                            toastOptions={{
                                style: {
                                    background: "#FFFFFF",
                                    color: "#2D2D2D",
                                    border: "2px solid #E86A5B",
                                    borderRadius: 0,
                                    fontFamily: "Satoshi, sans-serif",
                                    boxShadow: "6px 6px 0 0 #E86A5B",
                                },
                            }}
                        />
                        <AppRouter />
                    </AuthProvider>
                </UserAuthProvider>
            </BrowserRouter>
        </div>
    );
}
